import type { User } from '@/types/user/type';
import { useLocale } from 'next-intl';
import { useParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { fetchItem, initGuestSession } from '@/app/actions/server';
import { useApiErrorContext, useAuthenticatedContext } from '@/app/components/Provider';
import { redirect, usePathname } from '@/i18n/routing';
import { entityRoutes } from '~/const/appConst';
import { RequestStatus, useRequestState } from '~/hooks/request/useRequestState';

export function useGuest(
  check = true,
  interactionId?: number,
) {
  const pathname = usePathname();
  const params = useParams<{ id: string }>();
  const locale = useLocale();

  const { setError } = useApiErrorContext();
  const { setUser, setIsAuthenticated } = useAuthenticatedContext();

  const [allowed, setAllowed] = useState<boolean>(false);
  const [isModalOpened, setIsModalOpened] = useState(false);

  const { status, startRequest, handleError, handleSuccess }
    = useRequestState();

  const regex = /^\/(?:interactions|surveys)\/\d+$/;

  const surveyId = useMemo(
    () => {
      const matchSurveysPath = regex.exec(pathname) && +params.id;
      return interactionId || matchSurveysPath;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname, params.id, interactionId],
  );

  const init = async (id = surveyId) => {
    if (!id) {
      return;
    }

    await initGuestSession(id);

    const guest = await fetchItem<User>({
      pathname: 'guest_users/me',
      revalidate: 0,
    });

    setUser(guest);
    setIsAuthenticated(true);

    redirect({ href: { pathname: `/${entityRoutes.survey}/${id}` }, locale });
  };

  const isGuestModeAllowed = async (id = surveyId) => {
    if (!id) {
      return false;
    }

    startRequest();

    const response = await fetchItem<{ accessible: boolean }>({
      pathname: `interactions/${id}/guest_access`,
      revalidate: 0,
    });

    if (response['hydra:description']) {
      handleError();
      setError(response);
    }

    setAllowed(response.accessible);
    setIsModalOpened(response.accessible);
    handleSuccess();

    return response.accessible;
  };

  useEffect(() => {
    if (check) {
      isGuestModeAllowed();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [check]);

  return {
    id: surveyId,
    isFetching: status === RequestStatus.PENDING,
    allowed,
    isGuestModeAllowed,
    isModalOpened,
    init,
    setIsModalOpened,
  };
}
